import { inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { from, Observable, Subject } from 'rxjs';
import { switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import type { IListLabelUsageQuery, LabelUsageDto } from '@protocols/use-cases';

import { LabelConfigState, StagedLabelConfig } from '../state/label-config.state';
import { LabelConfigMultiAssignState, LabelUsageRow } from '../state/label-config-multi-assign.state';
import type { LabelConfigContextMethodType } from '../state/label-config-multi-assign.state';

/**
 * Read surface of the multi-assign label-config dialog: given a label, which
 * method types use it and what colour does each one currently have.
 */
export const LIST_LABEL_USAGE_QUERY = new InjectionToken<IListLabelUsageQuery>('LIST_LABEL_USAGE_QUERY');

export interface LabelConfigMultiAssignInput {
  labelUuid: string;
  contextMethodTypes: LabelConfigContextMethodType[];
  /**
   * The method type the dialog was opened FROM, when the opener has one in
   * context — its row opens pre-checked, loaded with that pair's colour. Absent
   * means "no method type in context" and the dialog opens blank, which is how
   * the standalone labels list and the standalone pipeline editor opt out.
   *
   * Carried explicitly rather than read off `contextMethodTypes`: that list
   * merges several sources and its order is incidental.
   *
   * Caller contract: the id must be one the merge can materialise a row for —
   * either the usage read returns it, or the opener also lists it in
   * `contextMethodTypes`. An id from neither source seeds nothing, silently.
   */
  contextMethodTypeId?: string;
}

/**
 * How the session ended. Bare on purpose — a submit has already written its
 * rows into the shared ledger, so there is no payload left to hand back.
 */
export type LabelConfigMultiAssignOutcome = 'submitted' | 'dismissed';

/**
 * Session effects of the multi-assign dialog. Injects the session state, the
 * SHARED staged-config ledger (resolved through the opener's injector) and the
 * usage query, and owns the two things the dialog does: load the checklist, and
 * stage one row per checked method type.
 */
@Injectable()
export class LabelConfigMultiAssignEffects implements OnDestroy {
  private readonly listLabelUsageQuery = inject(LIST_LABEL_USAGE_QUERY);

  /**
   * One-shot session outcome. Event, not state — the dialog host closes on the
   * first emission, and a late subscriber must never re-close a dialog.
   */
  private readonly _outcome$ = new Subject<LabelConfigMultiAssignOutcome>();
  public readonly outcome$: Observable<LabelConfigMultiAssignOutcome> = this._outcome$.asObservable();

  constructor(
    private readonly state: LabelConfigMultiAssignState,
    private readonly labelConfigState: LabelConfigState
  ) {}

  // Lifetime matches the dialog session (this class is a session provider), so
  // no outcome can outlive the dialog it belongs to.
  public ngOnDestroy(): void {
    this._outcome$.complete();
  }

  // ============================================================================
  // Effects
  // ============================================================================

  /**
   * Boots a session: resets the form, runs the usage query and publishes the
   * merged checklist. A load failure surfaces as an i18n key — the collapsed
   * error state — rather than an empty checklist that would read as
   * "this label is unused".
   */
  public readonly loadUsage = this.state.effect((input$: Observable<LabelConfigMultiAssignInput>) =>
    input$.pipe(
      tap((input: LabelConfigMultiAssignInput) => this.state.startSession(input.labelUuid)),
      switchMap((input) =>
        from(this.listLabelUsageQuery.execute({ labelUuid: input.labelUuid })).pipe(
          withLatestFrom(this.labelConfigState.stagedConfigs$),
          tapResponse(
            ([usage, staged]: [LabelUsageDto[], StagedLabelConfig[]]) => {
              this.state.setRows(this.mergeRows(input, usage, staged));
              // AFTER the rows, never before: the seed reads the merged colour, so
              // a config staged this session beats the persisted one. On the error
              // branch below nothing is seeded — the dialog collapses instead.
              if (input.contextMethodTypeId) this.state.seedFromContext(input.contextMethodTypeId);
            },
            () => this.state.setError('label_config_editor.errors.load_usage')
          )
        )
      )
    )
  );

  /**
   * Upserts one staged config per checked method type, all carrying the picked
   * colour, then ends the session. Unchecked rows are left alone — the
   * overwrite-checked-only rule. The rows only ever reach the shared ledger;
   * WHEN they persist is the opener's call (page-level save cascade, or an
   * immediate persist).
   */
  public readonly submit = this.state.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.state.labelUuid$, this.state.rows$, this.state.selectedIds$, this.state.pickedColor$),
      tap(
        ([, labelUuid, rows, selectedIds, pickedColor]: [
          void,
          string | null,
          LabelUsageRow[],
          string[],
          string | null
        ]) => {
          // Mirrors `canSubmit$` — the button is disabled in these states.
          if (!labelUuid || !pickedColor) return;

          const rowsById = new Map<string, LabelUsageRow>(rows.map((row) => [row.methodTypeId, row]));
          let staged = 0;
          for (const methodTypeId of selectedIds) {
            const row = rowsById.get(methodTypeId);
            if (!row) continue;
            this.labelConfigState.stageConfig({
              stagingId: uuidv4(),
              labelUuid,
              methodTypeId,
              color: pickedColor,
              originalConfigId: row.configId,
            });
            staged++;
          }

          // Counted, not inferred from `selectedIds`: a selection whose row is
          // gone stages nothing, and reporting that as a submit would close the
          // dialog as if work had been done.
          if (staged > 0) this._outcome$.next('submitted');
        }
      )
    )
  );

  /** Ends the session without staging anything — cancel, or the empty state's close. */
  public readonly dismiss = this.state.effect<void>((trigger$) =>
    trigger$.pipe(tap(() => this._outcome$.next('dismissed')))
  );

  // ============================================================================
  // Row merge
  // ============================================================================

  /**
   * Folds the three sources of truth into one checklist, name-ascending:
   *
   * - the usage query establishes the row set and supplies persisted
   *   config id + colour;
   * - the opener's context rows win on display `name`/`type` (a rename staged
   *   this session would otherwise render with its stale backend name) and add
   *   rows for associations that do not exist in the database yet;
   * - this session's staged configs win on colour + config id, so re-opening
   *   the dialog shows the work already staged rather than the backend value.
   */
  private mergeRows(
    input: LabelConfigMultiAssignInput,
    usage: LabelUsageDto[],
    staged: StagedLabelConfig[]
  ): LabelUsageRow[] {
    const rowsById = new Map<string, LabelUsageRow>(
      usage.map((row) => [
        row.methodTypeId,
        {
          methodTypeId: row.methodTypeId,
          name: row.name,
          type: row.type,
          configId: row.configId,
          currentColor: row.color,
        },
      ])
    );

    for (const context of input.contextMethodTypes) {
      const existing = rowsById.get(context.id);
      rowsById.set(context.id, {
        ...(existing ?? { methodTypeId: context.id }),
        name: context.name,
        type: context.type ?? existing?.type,
        pending: context.pending,
      });
    }

    /**
     * DELIBERATE DUPLICATION: the "staged config supersedes the stored colour"
     * precedence is also applied by `labelColorsByUuid$` in
     * pipeline-category.effects.ts — on the transposed axis (one label across many
     * method types here, one method type across many labels there) and producing a
     * different output shape. Sharing it would mean relocating `StagedLabelConfig`
     * into @protocols/domain, which is disproportionate for a two-line kernel.
     * A CHANGE TO THE PRECEDENCE MUST BE APPLIED IN BOTH PLACES.
     */
    for (const config of staged) {
      if (config.labelUuid !== input.labelUuid) continue;
      const existing = rowsById.get(config.methodTypeId);
      if (!existing) continue;
      rowsById.set(config.methodTypeId, {
        ...existing,
        currentColor: config.color,
        configId: config.originalConfigId,
      });
    }

    return Array.from(rowsById.values()).sort((a, b) => a.name.localeCompare(b.name));
  }
}
